import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grow,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";

type Props = {
  question: string | undefined;
  helpReply: string;
  setHelpReply: (value: string) => void;
  helpReplySent: boolean;
  onSendHelp: (answer: string) => void;
};

const ModernHelpField: React.FC<Props> = ({
  question,
  helpReply,
  setHelpReply,
  helpReplySent,
  onSendHelp,
}) => {
  const { t } = useTranslation();
  const [isTyping, setIsTyping] = useState(true);
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submittedAnswer, setSubmittedAnswer] = useState(helpReply || ""); // capture the sent reply
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const validQuestion = Boolean(question && question.trim().length > 0);

  useEffect(() => {
    if (!validQuestion || helpReplySent) return;
    const timer = setTimeout(() => {
      if (currentIndex < question!.length) {
        setDisplayedText(question!.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      } else {
        setIsTyping(false);
      }
    }, 15);
    return () => clearTimeout(timer);
  }, [currentIndex, question, helpReplySent]);

  useEffect(() => {
    if (validQuestion) {
      setIsTyping(true);
      setDisplayedText("");
      setCurrentIndex(0);
      setSubmittedAnswer(helpReply || "");
    }
  }, [question]);

  useEffect(() => {
    if (!isTyping && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isTyping]);

  useEffect(() => {
    if (containerRef.current && validQuestion) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  if (!validQuestion) return null;

  const handleSend = (answer: string) => {
    setSubmittedAnswer(answer);
    onSendHelp(answer);
  };

  return (
    <Grow in timeout={500}>
      <Paper
        ref={containerRef}
        elevation={0}
        sx={{
          borderRadius: "0.5em",
          backgroundColor: "#fff8e1",
          border: "1px solid #ffe082",
          mb: "1em",
          overflow: "hidden",
          maxWidth: "600px",
          mx: "auto",
        }}
      >
        {/* header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1em",
            p: "1em",
            borderBottom: "1px solid #ffe082",
            backgroundColor: "#fff8e1",
          }}
        >
          <WarningAmberIcon sx={{ color: "#f9a825" }} />
          <Typography variant="subtitle2" fontWeight={600} color="#4e342e">
            {t("needHelpTitle") + " " + question || ""}
          </Typography>
          <Tooltip title={t("needHelpTooltip")}>
            <IconButton size="small" sx={{ ml: "auto", color: "#6d4c41" }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Question Bubble */}
        <Box sx={{ p: "1em" }}>
          <Typography variant="body2" color="#5d4037" sx={{ lineHeight: 1.5 }}>
            {displayedText}
          </Typography>
        </Box>

        {/* Input vs. Answer display */}
        {!helpReplySent ? (
          <Box sx={{ p: "1em", pt: 0 }}>
            {/* Input area */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="#5d4037"
              sx={{ mb: "0.5em" }}
            >
              {t("yourAnswer")}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              inputRef={textareaRef}
              placeholder={t("typeHere")}
              value={helpReply}
              onChange={e => setHelpReply(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "6px",
                  transition: "all 0.3s ease",
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.2)",
                  },
                  "& fieldset": { borderColor: "rgba(0, 0, 0, 0.1)" },
                  "&:hover fieldset": { borderColor: "#f59e0b" },
                },
              }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mt: "1em" }}
            >
              <Button
                variant="contained"
                disableElevation
                disabled={!helpReply.trim()}
                onClick={() => handleSend(helpReply.trim())}
                endIcon={<SendIcon />}
                sx={{
                  bgcolor: "#f59e0b",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: "6px",
                  px: "1.5em",
                  py: "0.4em",
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: "#d97706" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(245, 158, 11, 0.3)",
                    color: "rgba(255,255,255,0.8)",
                  },
                  textTransform: "none",
                }}
              >
                {t("sendAnswer")}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: "1em", pt: 0, backgroundColor: "#fff8e1" }}>
            {/* Display the captured answer */}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="#006064"
              sx={{ mb: "0.5em" }}
            >
              {t("yourAnswer")}
            </Typography>
            <Paper elevation={1} sx={{ p: "0.75em" }}>
              <Typography variant="body2" color="#004d40">
                {submittedAnswer}
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    </Grow>
  );
};

export default ModernHelpField;
